import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Seo, { ORG_LD } from "@/components/site/Seo";
import { RevealLines } from "@/components/site/RevealText";


const INDUSTRIES = [
  {
    key: "oil-gas",
    title: "Oil & Gas",
    lead: "Upstream, midstream and downstream sourcing — from wellhead assemblies and flow iron to line pipe, valves, coated spools and MRO consumables.",
    scope: ["Wellhead components (API 6A)", "Flow iron and manifolds (API 16C)", "Valves (API 6D / 6A / ASME B16.34)", "Line pipe, coated spools, induction bends", "OCTG tubing and casing", "MRO consumables for rigs and platforms"],
  },
  {
    key: "energy",
    title: "Energy",
    lead: "Sourcing for power generation, transmission and renewable projects — mechanical equipment, electrical infrastructure and specialty materials.",
    scope: ["Switchgear and transformers", "Cable, glands and terminations", "Structural steel and enclosures", "Balance-of-plant mechanical components", "Instrumentation and control hardware", "Solar and wind BOS components"],
  },
  {
    key: "manufacturing",
    title: "Industrial Manufacturing",
    lead: "Support for OEMs and contract manufacturers who need reliable global sourcing of engineered components and materials.",
    scope: ["CNC-machined parts to drawings", "Sheet metal fabrication and enclosures", "Motors, gearboxes and drives", "Bearings, seals and drive components", "Injection-moulded and cast parts", "Consumables and packaging"],
  },
  {
    key: "engineering-epc",
    title: "Engineering & EPC",
    lead: "Package procurement for engineering contractors — technical specification-driven sourcing across mechanical, electrical and structural scopes.",
    scope: ["Process equipment and skids", "Structural steel and pipe supports", "Valves, actuators and instrumentation", "Electrical panels, lighting and cable", "HVAC and utility packages", "Vendor documentation and MTC support"],
  },
  {
    key: "mining",
    title: "Mining & Minerals",
    lead: "Wear parts, spares and specialty steels for mining, mineral processing and materials handling operations.",
    scope: ["Wear plates and abrasion-resistant steels", "Crusher and mill spares", "Conveyor components", "Hydraulic hoses and fittings", "Ground engaging tools", "Consumables and PPE"],
  },
  {
    key: "infrastructure",
    title: "Infrastructure",
    lead: "Sourcing for construction, civils and infrastructure projects — from structural steel to electrical distribution and specialty materials.",
    scope: ["Structural sections and rebar", "Precast components", "MEP and electrical distribution", "Waterworks: pipe, valves, fittings", "Traffic and safety equipment", "Site consumables"],
  },
  {
    key: "chemicals",
    title: "Chemicals & Petrochemicals",
    lead: "Corrosion-resistant materials, specialty piping, valves and process equipment for chemical and petrochemical plants.",
    scope: ["CRA piping and fittings (Duplex, Super-Duplex, Inconel)", "Specialty valves for aggressive service", "Coated equipment (FBE, 3LPE, PTFE)", "Reactors, columns and heat exchangers", "Skid packages", "Pressure vessels (ASME VIII)"],
  },
  {
    key: "general",
    title: "General Industrial Procurement",
    lead: "Whatever the industry, if the requirement is technical and hard to source through normal channels — we can help.",
    scope: ["Ad-hoc project procurement", "Long-lead spares for shutdowns / turnarounds", "Obsolete or discontinued parts", "Reverse-engineered components", "Multi-item consolidated shipments", "Documentation and export support"],
  },
];


export default function Industries() {
  return (
    <div className="bg-nx-bg text-nx-ink" data-testid="page-industries">
      <Seo
        title="Industries · Oil & Gas, Energy, Manufacturing, EPC, Mining, Infrastructure"
        description="NEXORIQ sources for oil & gas, energy, industrial manufacturing, engineering & EPC, mining, infrastructure, chemicals & petrochemicals, and general industrial procurement."
        path="/industries"
        structuredData={ORG_LD}
      />
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-24">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Industries</div>
        <RevealLines
          as="h1"
          lines={[<>Wherever a requirement</>, <>is <span className="text-nx-copper">technical</span>.</>]}
          className="font-cabinet font-black tracking-[-0.04em] leading-[0.92] text-[13vw] md:text-[7vw] lg:text-[5.4vw] text-nx-bg"
        />
        <p className="mt-8 max-w-2xl text-nx-bg/75 leading-relaxed text-lg">
          NEXORIQ serves industrial buyers whose sourcing is routine some months and impossible in
          others. Below is a snapshot of where we most often work.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24">
        <div className="space-y-20 md:space-y-24">
          {INDUSTRIES.map((ind, i) => (
            <motion.article
              key={ind.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.7 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 border-t border-nx-navy/20 pt-10"
              data-testid={`industry-${ind.key}`}
            >
              <div className="lg:col-span-4">
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-nx-copper">Industry {String(i + 1).padStart(2, "0")}</span>
                <h2 className="mt-3 font-cabinet font-black tracking-[-0.03em] leading-[0.95] text-3xl md:text-4xl text-nx-navy">
                  {ind.title}
                </h2>
              </div>
              <div className="lg:col-span-8 lg:pl-8 lg:border-l lg:border-nx-navy/20">
                <p className="text-nx-navy2 text-lg leading-relaxed max-w-2xl">{ind.lead}</p>
                <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                  {ind.scope.map((s) => (
                    <li key={s} className="text-nx-navy2 leading-relaxed">
                      <span className="text-nx-copper mr-2">→</span>{s}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5.4vw] text-nx-bg">
              Your industry not listed?
            </h2>
            <p className="mt-6 text-nx-bg/75 max-w-xl leading-relaxed">
              Our approach is industry-agnostic: if it is industrial and technical, we can probably
              source it. Send the requirement across and we'll respond honestly.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/sourcing-request" className="nx-btn on-dark" data-testid="industries-cta">
              Submit a Requirement <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
